import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const whyMobikodeDifferentiators = [
  {
    id: "01",
    title: "End-to-End Transformation",
    short: "Full lifecycle discovery & modernization",
    description:
      "Full lifecycle coverage spanning strategic discovery, multi-module implementation, architecture modernization, and operational adoption.",
    highlight: "Complete Lifecycle Coverage",
    gradient: "from-[#E8ECF8] via-[#ECEEFA] to-[#D5DCF5]",
  },
  {
    id: "02",
    title: "Enterprise Technology Breadth",
    short: "Proven cross-platform engineering",
    description:
      "Unlike narrow platform agencies, we bring proven enterprise engineering expertise across complex distributed application landscapes.",
    highlight: "Distributed Architecture Depth",
    gradient: "from-[#E2E6EE] via-[#E8EDF4] to-[#C9D4E2]",
  },
  {
    id: "03",
    title: "Strategic Consulting & Advisory",
    short: "Maturity blueprints & process optimization",
    description:
      "Practical maturity blueprints, business process optimization, and module prioritization structured around measurable enterprise KPIs.",
    highlight: "Measurable KPI Roadmaps",
    gradient: "from-[#DFF2EB] via-[#E6F4EF] to-[#BEE6D8]",
  },
  {
    id: "04",
    title: "Custom Engineering & Integration",
    short: "Integration Hub, REST/SOAP & App Engine",
    description:
      "Deep Integration Hub proficiency, REST/SOAP connectivity, and bespoke app engineering via App Engine for specialized workflows.",
    highlight: "Zero Data Silos",
    gradient: "from-[#D8E8F5] via-[#E2F0F9] to-[#BAD8EE]",
  },
  {
    id: "05",
    title: "AI & Intelligent Automation",
    short: "GenAI, Now Assist & Virtual Agents",
    description:
      "Embedding Generative AI, Now Assist capabilities, and Virtual Agents to eliminate manual overhead and streamline execution speed.",
    highlight: "Intelligent Automation",
    gradient: "from-[#EAE7F8] via-[#EFEBF9] to-[#D5CFF3]",
  },
  {
    id: "06",
    title: "Flexible Staff Augmentation",
    short: "Certified developers & dedicated pods",
    description:
      "Rapid deployment of certified ServiceNow developers, solution architects, and dedicated delivery pods to scale project bandwidth.",
    highlight: "Rapid Team Ramp-up",
    gradient: "from-[#E7ECF3] via-[#EFF3F8] to-[#CBD8E7]",
  },
  {
    id: "07",
    title: "Continuous Managed Support",
    short: "24/7 L1–L3 SLA & health inspections",
    description:
      "Round-the-clock L1–L3 SLA management, preventive release upgrades, health inspections, and continuous platform optimization.",
    highlight: "24/7 Platform Health",
    gradient: "from-[#E0F0F3] via-[#E9F6F8] to-[#C2E4EA]",
  },
  {
    id: "08",
    title: "Complex Ecosystem Mastery",
    short: "Multi-vendor audited infrastructures",
    description:
      "Decades of collective experience modernizing heavily audited, multi-vendor enterprise infrastructures without workflow interruption.",
    highlight: "Audited & Regulated Ready",
    gradient: "from-[#F2EDE6] via-[#F7F4EE] to-[#E3D9C9]",
  },
  {
    id: "09",
    title: "Business-Centric Architecture",
    short: "Direct alignment to business impact",
    description:
      "Direct alignment of digital workflows to measurable business impact rather than forcing organizations into rigid boilerplate patterns.",
    highlight: "Outcome-Driven Design",
    gradient: "from-[#E6EEF5] via-[#EEF4F9] to-[#CDDEEE]",
  },
  {
    id: "10",
    title: "Scalable Engagement Models",
    short: "Agile milestone-driven delivery",
    description:
      "Agile, outcome-driven milestones and flexible delivery structures built to adapt alongside your evolving enterprise needs.",
    highlight: "Adaptable Milestones",
    gradient: "from-[#F0EDF7] via-[#F6F3FA] to-[#DDD6EE]",
  },
];

function WhyMobikodeSection() {
  return (
    <section className="w-full bg-white">
      <div className="mx-auto max-w-[1440px] px-[20px] lg:px-[80px]">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-10 lg:gap-16">

          <div className="w-full lg:w-full max-w-[444px] lg:sticky lg:top-32">
            <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
              Consulting capabilities
            </p>
            <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.2] tracking-tight mb-5">
              ServiceNow expertise backed by broader technology capabilities
            </h2>
            <p className="font-graphik text-[14px] lg:text-[16px] font-[500] text-[#49577A] leading-[1.7] max-w-[440px]">
              Unlike a ServiceNow-only implementation approach, Mobikode combines
              ServiceNow with Enterprise AI, Product Engineering, integrations and
              digital transformation capabilities.
            </p>
          </div>

          <div className="w-full lg:w-full max-w-[742px] flex flex-col gap-[14px] sm:gap-[20px]">
            {whyMobikodeDifferentiators.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className={`relative rounded-sm bg-gradient-to-r ${item.gradient} p-7 sm:p-9 lg:p-10 transition-all duration-300 hover:-translate-y-1`}
              >
                {/* Top Watermark Number */}
                <span className="absolute top-4 right-6 sm:top-6 sm:right-8 font-sora text-[32px] sm:text-[44px] font-bold text-[#01386A]/10 select-none pointer-events-none">
                  {item.id}
                </span>

                <div className="relative z-10 max-w-[560px]">
                  <h3 className="font-sora text-[20px]  lg:text-[30px] font-[600] text-[#01386A] tracking-tight mb-3">
                    {item.title}
                  </h3>

                  <p className="font-graphik text-[14px] lg:text-[16px] text-[#01386A] leading-[1.65] font-[500] mb-6">
                    {item.description}
                  </p>

                  <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-white/80 px-3.5 py-1.5 rounded-full">
                    <CheckCircle2 size={15} className="text-[#ED6800] shrink-0" />
                    <span className="font-graphik text-[12px] sm:text-[14px] font-[500] text-[#01386A]">
                      {item.highlight}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}

export default WhyMobikodeSection;